import { ChangeDetectionStrategy, Component, OnInit, computed, signal } from '@angular/core';
import { HelpdeskDb } from 'lib/cosmos/helpdesk/helpdeskDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { linkedItemsHelper } from 'lib/helpers/linked-items-helper.class.js';
import { nameHelper } from 'lib/helpers/name-helper.js';
import { IButton } from 'lib/ui/buttons/index.js';
import { SelectionDialogItem } from 'lib/ui/dialog/index.js';
import { ListItem } from 'lib/ui/list/index.js';
import { Observable, map, of } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

@Component({
	templateUrl: './helpdesk-list.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class HelpdeskListComponent extends TypeCoreComponent implements OnInit {
	public readonly userListItems = signal<readonly ListItem<HelpdeskDb.IHelpdeskItem>[] | undefined>(undefined);
	public readonly message = signal<string>('');
	public readonly users = signal<readonly UserDb.IUserItem[] | undefined>(undefined);

	public readonly helpdeskButtons = computed<readonly IButton[]>(() => {
		return [
			{
				title: `Create conversation`,
				icon: 'add',
				buttonType: 'primary',
				click: () => {
					this.openSelectUserDialog();
				}
			}
		];
	});

	constructor() {
		super();

		this.dataInit = () => {
			this.initHelpdeskItems();
		};
	}

	private initHelpdeskItems(): void {
		super.subscribeToObservable(
			this.dependencies.httpService
				.get<readonly HelpdeskDb.IHelpdeskItem[]>((builder) =>
					builder.withCustomApiRoute('helpdesk', 'listHelpdeskItems').build()
				)
				.pipe(
					map((response) => {
						const userListItems = response.data
							.map<ListItem<HelpdeskDb.IHelpdeskItem>>((m) => {
								const user = linkedItemsHelper.getLinkedItemById<UserDb.IUserItem>(
									response.linkedItems ?? [],
									m.userId
								);
								const latestMessage = m?.messages?.[0];

								return {
									id: m.id,
									title: nameHelper.getUserName(user?.email, user?.firstName, user?.lastName),
									subtitle: latestMessage?.message,
									avatar: user?.avatar,
									routerLink: this.dependencies.navigateService
										.actionRoute(
											(routes) => routes.helpdeskRoutes.conversation.children.form,
											m.userId
										)
										.getUrl(),
									fromNowDate: latestMessage?.created,
									data: m
								};
							})
							// sort by last message date
							.toSorted((a, b) => {
								return (
									(b.fromNowDate ? new Date(b.fromNowDate) : new Date(1900, 1)).getTime() -
									(a.fromNowDate ? new Date(a.fromNowDate) : new Date(1900, 1)).getTime()
								);
							});

						this.userListItems.set(userListItems);
					})
				)
		);
	}

	private initUsers(): Observable<readonly UserDb.IUserItem[]> {
		const users = this.users();
		if (users) {
			return of(users);
		}

		return this.httpService
			.get<readonly UserDb.IUserItem[]>((builder) =>
				builder.withCustomApiRoute('user', 'listUsersForAdmin').build()
			)
			.pipe(
				map((response) => {
					this.users.set(response.data);

					return response.data;
				})
			);
	}

	private openSelectUserDialog(): void {
		super.subscribeToObservable(
			this.initUsers().pipe(
				map((users) => {
					const userListItems = this.userListItems() ?? [];

					this.dependencies.dialogService.openSelectionDialog({
						title: `Select user`,
						subtitle: `Conversation will be created for selected user`,
						items: users
							.filter((user) => {
								if (userListItems.find((m) => m.data.userId === user.id)) {
									// conversation already exists for this user
									return false;
								}
								if (
									user.userType === 'system' ||
									user.isTrainerTemplate ||
									user.isClientTemplate ||
									user.isDemoClient
								) {
									return false;
								}
								return true;
							})
							.map((user) => {
								const selectionItem: SelectionDialogItem<UserDb.IUserItem> = {
									data: user,
									id: user.id,
									title: user.system.name,
									avatar: user.avatar
								};

								return selectionItem;
							}),
						select: (item) => {
							this.createConversation(item);
						}
					});
				})
			)
		);
	}

	private createConversation(user: UserDb.IUserItem): void {
		super.subscribeToObservable(
			this.httpService
				.get<HelpdeskDb.IHelpdeskItem>((builder) =>
					builder.withCustomApiRoute('helpdesk', 'getHelpdeskData').withId(user.id).build()
				)
				.pipe(
					map((response) => {
						super
							.actionRoute((routes) => routes.helpdeskRoutes.conversation.children.form, user.id)
							.navigate();
					})
				)
		);
	}
}
